import React from 'react';

import { trimValue } from '../../services/helpers/StringHelpers';
import { ZeosFontSize } from '../../services/helpers/ThemeHelpers';
import { ZeosLabel } from './ZeosLabel';

interface IProps {
  value?: string | null;
  autoComplete?: string;
  defaultValue?: any;
  disabled?: boolean;
  forwardRef?: React.Ref<HTMLInputElement>;
  label?: string;
  name: string;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  type?: "text" | "password" | "email" | "number" | "search" | "tel" | "url";
  readOnly?: boolean;
  error?: string;
  size?: "sm" | "md" | "lg";
  mode?: "input" | "display";
  required?: boolean;
}

export const ZeosInput = (props: IProps) => {
  const {
    value: valueProp,
    autoComplete,
    defaultValue,
    disabled,
    forwardRef,
    label,
    name,
    onChange,
    onBlur,
    placeholder,
    type = "text",
    readOnly,
    size = "md",
    error,
    mode = "input",
    required = false,
  } = props;

  // Normalize null/undefined to empty string to prevent React warnings
  const value = valueProp ?? "";
  const sizeMap = {
    sm: "w-[12.5rem]",
    md: "w-[25rem]",
    lg: "w-[50rem]",
  };

  const hasError = (error ?? "") !== "";

  if (mode === "display") {
    // Render display-only version
    return (
      <div className="mb-4">
        {label && (
          <p className="text-sm text-zeos-grey-md mb-1 font-semibold">
            {label}
          </p>
        )}
        <p className="text-base text-zeos-black">{value ?? "-"}</p>
      </div>
    );
  }

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const trimmed = trimValue(e.target.value);
    if (trimmed !== e.target.value) {
      onChange?.({ target: { name, value: trimmed } } as any);
    }
    onBlur?.(e);
  };

  // Regular input mode
  return (
    <div className="mb-4">
      {label && <ZeosLabel label={label} htmlFor={name} required={required} />}
      <input
        id={name}
        name={name}
        type={type}
        autoComplete={autoComplete}
        placeholder={placeholder}
        defaultValue={defaultValue}
        value={value}
        onChange={onChange}
        onBlur={handleBlur}
        disabled={disabled}
        readOnly={readOnly}
        ref={forwardRef}
        className={`
          h-[3.125rem] px-4 w-full border 
          ${hasError ? "border-zeos-red" : "border-zeos-grey-md"}
          ${
            disabled || readOnly
              ? "bg-zeos-grey-lt cursor-not-allowed"
              : "bg-white"
          } text-${
          ZeosFontSize.lg
        } text-zeos-black font-light font-sans focus:outline-none focus:ring-2 focus:ring-zeos-blue transition-all duration-150
        `}
      />
      {hasError && <p className="mt-1 text-xs text-zeos-orange-dk">{error}</p>}
    </div>
  );
};
